import React, { useState, useEffect } from 'react';
import { apiGet } from '../../api/apiClient';
import { toastError } from '../common/toast';
import { useSessionInfo } from '../../hooks/useSessionInfo';

const InvoicePrint = ({ invoiceId, onClose }) => {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const { displayName, clientName, addressLine1, addressLine2, city, state, postalCode } = useSessionInfo();

  useEffect(() => {
    if (invoiceId) {
      loadInvoiceForPrint();
    }
  }, [invoiceId]);

  const loadInvoiceForPrint = async () => {
    try {
      setLoading(true);
      const response = await apiGet(`/invoicing/invoices/${invoiceId}`);
      if (response && response.data) {
        const invoiceData = response.data;
        
        // Fetch payment data for this invoice
        try {
          const paymentsResponse = await apiGet(`/payments?invoice_id=${invoiceId}&status=completed`);
          const payments = paymentsResponse?.data || [];
          
          // Calculate total paid amount
          const amountPaid = payments.reduce((total, payment) => total + (payment.amount || 0), 0);
          
          // Add amount_paid to invoice data
          invoiceData.amount_paid = amountPaid;
        } catch (paymentError) {
          console.warn('Failed to fetch payment data for print:', paymentError);
          invoiceData.amount_paid = 0;
        }
        
        setInvoice(invoiceData);
        // Generate and open print window once data is loaded
        setTimeout(() => {
          generateAndPrint(invoiceData);
        }, 100);
      }
    } catch (error) {
      console.error('Error loading invoice for print:', error);
      toastError('Failed to load invoice for printing. Please try again.');
      if (onClose) onClose();
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatRoomDetails = (room, roomName) => {
    if (roomName) return roomName;
    if (room?.room_name) return room.room_name;
    if (!room) return 'N/A';
    return room.room_type || 'N/A';
  };

  const generateAndPrint = (invoiceData) => {
    const invoiceHTML = generateInvoiceHTML(invoiceData);
    
    // Open a new window with the invoice content
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    printWindow.document.write(invoiceHTML);
    printWindow.document.close();
    
    // Wait for content to load, then print
    printWindow.onload = function() {
      printWindow.focus();
      printWindow.print();
      
      // Close the print window after printing (optional)
      printWindow.onafterprint = function() {
        printWindow.close();
        if (onClose) onClose();
      };
    };

    // Fallback: close this component after a delay if onClose is provided
    if (onClose) {
      setTimeout(() => {
        onClose();
      }, 1000);
    }
  };

  const generateInvoiceHTML = (invoiceData) => {
    const invoiceItemsRows = invoiceData.invoice_items && invoiceData.invoice_items.length > 0
      ? invoiceData.invoice_items.map(item => `
          <tr>
            <td style="padding: 12px; border-right: 1px solid #dee2e6; vertical-align: top;">
              <strong>${item.description || item.invoice_item_type}</strong>
              ${item.discount_amount > 0 ? `
                <div style="color: #28a745; font-size: 0.8rem; font-style: italic;">
                  Discount: ${formatCurrency(item.discount_amount)}
                  ${item.discount_description ? ` (${item.discount_description})` : ''}
                </div>
              ` : ''}
            </td>
            <td style="padding: 12px; border-right: 1px solid #dee2e6; text-align: right;">${item.quantity}</td>
            <td style="padding: 12px; border-right: 1px solid #dee2e6; text-align: right;">${formatCurrency(item.amount)}</td>
            <td style="padding: 12px; border-right: 1px solid #dee2e6; text-align: right;">${formatCurrency(item.tax_amount)}</td>
            <td style="padding: 12px; text-align: right;">${formatCurrency(item.total_amount)}</td>
          </tr>
        `).join('')
      : '<tr><td colspan="5" style="padding: 20px; text-align: center; color: #6c757d; font-style: italic;">No invoice items found</td></tr>';

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice ${invoiceData.invoice_number}</title>
        <meta charset="utf-8">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            line-height: 1.6; 
            color: #333; 
            max-width: 800px; 
            margin: 0 auto; 
            padding: 20px; 
          }
          .invoice-container { 
            background: white; 
            padding: 40px; 
            border-radius: 8px; 
            box-shadow: 0 2px 8px rgba(0,0,0,0.1); 
          }
          .invoice-header { 
            display: flex; 
            justify-content: space-between; 
            align-items: flex-start; 
            margin-bottom: 40px; 
            padding-bottom: 20px; 
            border-bottom: 2px solid #e9ecef; 
          }
          .invoice-title { 
            font-size: 2.5rem; 
            font-weight: 700; 
            color: #2c3e50; 
            margin-bottom: 8px; 
          }
          .invoice-number { 
            font-size: 1.2rem; 
            color: #6c757d; 
            margin-bottom: 12px; 
            font-weight: 600;
          }
          .status-badge { 
            display: inline-block; 
            padding: 6px 12px; 
            border-radius: 20px; 
            font-size: 0.8rem; 
            font-weight: 600; 
            text-transform: uppercase; 
            border: 1px solid #000; 
            background: white; 
            color: #000; 
          }
          .invoice-dates p { 
            margin: 4px 0; 
            color: #6c757d; 
          }
          .billing-section { 
            display: grid; 
            grid-template-columns: 1fr 1fr; 
            gap: 40px; 
            margin-bottom: 30px; 
          }
          .billing-section h4 { 
            font-weight: 600; 
            color: #2c3e50; 
            margin-bottom: 12px; 
            text-transform: uppercase; 
          }
          .billing-details strong { 
            font-size: 1.1rem; 
            color: #2c3e50; 
            display: block; 
            margin-bottom: 4px; 
          }
          .billing-details p { 
            margin: 2px 0; 
            color: #6c757d; 
          }
          .billing-period { 
            margin-bottom: 30px; 
            padding: 16px 20px; 
            background: #f8f9fa; 
            border-radius: 6px; 
            border-left: 4px solid #4f46e5; 
          }
          .billing-period h4 { 
            font-weight: 600; 
            color: #2c3e50; 
            margin-bottom: 8px; 
          }
          .invoice-items-section { 
            margin-bottom: 30px; 
          }
          .invoice-items-section h4 { 
            font-size: 1.2rem; 
            font-weight: 600; 
            color: #2c3e50; 
            margin-bottom: 20px; 
          }
          .items-table { 
            width: 100%; 
            border-collapse: collapse; 
            border: 1px solid #dee2e6; 
            border-radius: 6px; 
            overflow: hidden; 
          }
          .items-table th { 
            background: #f8f9fa; 
            padding: 12px; 
            border-right: 1px solid #dee2e6; 
            font-weight: 600; 
            color: #495057; 
            text-transform: uppercase; 
            border-bottom: 1px solid #dee2e6; 
          }
          .items-table th:last-child { border-right: none; }
          .items-table td { 
            padding: 12px; 
            border-right: 1px solid #dee2e6; 
            border-bottom: 1px solid #f1f3f4; 
            vertical-align: top; 
          }
          .items-table td:last-child { border-right: none; }
          .items-table tr:last-child td { border-bottom: none; }
          .invoice-total-section { 
            margin-bottom: 30px; 
            padding: 20px 0; 
            border-top: 2px solid #e9ecef; 
          }
          .total-row { 
            display: flex; 
            justify-content: flex-end; 
            align-items: center; 
            gap: 20px; 
            margin-bottom: 10px;
          }
          .total-row.final-total {
            margin-top: 15px;
            padding-top: 15px;
            border-top: 1px solid #dee2e6;
          }
          .total-row.final-total .print-invoice-total-label {
            font-size: 1.1rem;
            font-weight: 700;
          }
          .total-row.final-total .print-invoice-total-amount {
            font-size: 1rem;
            font-weight: 800;
            background: #fff3cd;
            border: 2px solid #ffeaa7;
            color: #856404;
          }
          .print-invoice-total-label { 
            font-size: 1rem; 
            font-weight: 600; 
            color: #2c3e50; 
          }
          .print-invoice-total-amount { 
            font-size: 1rem; 
            font-weight: 700; 
            color: #28a745; 
            background: #f8f9fa; 
            padding: 6px 12px; 
            border-radius: 6px; 
            border: 2px solid #e9ecef; 
          }
          .invoice-footer { 
            text-align: center; 
            padding-top: 20px; 
            border-top: 1px solid #e9ecef; 
            color: #6c757d; 
          }
          @media print {
            body { margin: 0; padding: 0; }
            .invoice-container { 
              box-shadow: none; 
              border-radius: 0; 
              padding: 20px; 
            }
            .print-invoice-total-amount {
              background: none !important;
              border: 1px solid #dee2e6 !important;
              color: #2c3e50 !important;
            }
            .total-row.final-total .print-invoice-total-amount {
              background: none !important;
              border: 2px solid #dee2e6 !important;
              color: #2c3e50 !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          <div class="invoice-header">
            <div class="invoice-header-left">
              <h1 class="invoice-title">INVOICE</h1>
              <div class="invoice-number">#${invoiceData.invoice_number}</div>
            </div>
            <div class="invoice-header-right">
              <div class="invoice-dates">
                <p><strong>Invoice Date:</strong> ${formatDate(invoiceData.invoice_date)}</p>
                <p><strong>Amount Paid:</strong> ${formatCurrency(invoiceData.amount_paid || 0)}</p>
                <p><strong>Amount Remaining:</strong> ${formatCurrency((invoiceData.total_amount || 0) - (invoiceData.amount_paid || 0))}</p>
              </div>
            </div>
          </div>

          <div class="billing-section">
            <div class="billing-from">
              <h4>From:</h4>
              <div class="billing-details">
                <strong>${clientName || displayName}</strong>
                ${addressLine1 ? `<p>${addressLine1}</p>` : ''}
                ${addressLine2 ? `<p>${addressLine2}</p>` : ''}
                ${city || state || postalCode ? `<p>${[city, state].filter(Boolean).join(', ')}${postalCode ? ' - ' + postalCode : ''}</p>` : ''}
              </div>
            </div>
            <div class="billing-to">
              <h4>Bill To:</h4>
              <div class="billing-details">
                <strong>${invoiceData.paying_guest?.name || 'N/A'}</strong>
                ${invoiceData.paying_guest?.address && invoiceData.paying_guest.address !== 'N/A' ? `<p>${invoiceData.paying_guest.address}</p>` : ''}
                ${invoiceData.paying_guest?.mobile ? `<p>Phone: ${invoiceData.paying_guest.mobile}</p>` : ''}
                <p>${invoiceData.building?.name || 'N/A'}</p>
                <p>${formatRoomDetails(invoiceData.room, invoiceData.room_name)}</p>
              </div>
            </div>
          </div>

          <div class="billing-period">
            <h4>Billing Period:</h4>
            <p>${formatDate(invoiceData.billing_period_start)} to ${formatDate(invoiceData.billing_period_end)}</p>
          </div>

          <div class="invoice-items-section">
            <h4>Invoice Items</h4>
            <table class="items-table">
              <thead>
                <tr>
                  <th style="text-align: left;">Description</th>
                  <th style="text-align: right;">Qty</th>
                  <th style="text-align: right;">Amount</th>
                  <th style="text-align: right;">Tax</th>
                  <th style="text-align: right;">Total</th>
                </tr>
              </thead>
              <tbody>
                ${invoiceItemsRows}
              </tbody>
            </table>
          </div>

          <div class="invoice-total-section">
            <div class="total-row">
              <span class="print-invoice-total-label">Total Amount:</span>
              <span class="print-invoice-total-amount">${formatCurrency(invoiceData.total_amount)}</span>
            </div>
            <div class="total-row">
              <span class="print-invoice-total-label">Amount Paid:</span>
              <span class="print-invoice-total-amount">${formatCurrency(invoiceData.amount_paid || 0)}</span>
            </div>
            <div class="total-row final-total">
              <span class="print-invoice-total-label">Amount Remaining:</span>
              <span class="print-invoice-total-amount">${formatCurrency((invoiceData.total_amount || 0) - (invoiceData.amount_paid || 0))}</span>
            </div>
          </div>

          <div class="invoice-footer">
            <p><strong>Thank you for your business!</strong></p>
            <small>This is a computer-generated invoice.</small>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // This component doesn't render anything visible - it just handles the print process
  if (loading) {
    return null; // or a small loading indicator if needed
  }

  return null;
};

export default InvoicePrint;
